/**
 * Finnish Phase 2: machine subreasons and human rationale for taxable income rows.
 * Crypto.com `typeNorm` is lower_snake from the CSV transaction type column.
 */

export function fiSubreasonForCryptoComIncome(typeNorm: string): string {
  const t = typeNorm.toLowerCase();
  if (t === 'rewards_platform_deposit_credited') return 'CDC_MISSION_REWARD';
  if (t === 'card_cashback' || t === 'referral_card_cashback') return 'CDC_CARD_CASHBACK';
  if (t === 'mco_stake_reward') return 'CDC_MCO_STAKE_REWARD';
  if (t === 'reimbursement') return 'CDC_REIMBURSEMENT';
  if (t === 'admin_wallet_credited') return 'CDC_ADMIN_CREDIT';
  if (t === 'referral_gift') return 'CDC_REFERRAL_GIFT';
  if (t.includes('non_compound_interest')) return 'CDC_EARN_NON_COMPOUND_INTEREST';
  if (t.includes('mystery_box')) return 'CDC_MYSTERY_BOX';
  if (t.includes('cro_pool_reward')) return 'CDC_CRO_POOL_REWARD';
  if (t.includes('supercharger_reward')) return 'CDC_SUPERCHARGER_REWARD';
  if (t.includes('trading_rebate')) return 'CDC_TRADING_REBATE';
  if (t.includes('cro_lockup_reward')) return 'CDC_LOCKUP_REWARD';
  if (t.includes('card_rebate:')) return 'CDC_CARD_REBATE';
  if (t.includes('competition_reward')) return 'CDC_COMPETITION_REWARD';
  if (t.includes('campaign_reward')) return 'CDC_CAMPAIGN_REWARD';
  if (t.includes('nft_payout')) return 'CDC_NFT_PAYOUT';
  if (t.includes('referral_bonus_reward') || t.includes('referral_bonus')) return 'CDC_REFERRAL_BONUS';
  if (t.includes('pay_rewards') || t.includes('pay_reward')) return 'CDC_PAY_REWARDS';
  if (t.includes('sign-up_bonus')) return 'CDC_SIGNUP_BONUS';
  if (t.includes('adjustment_(credit)')) return 'CDC_ADJUSTMENT_CREDIT';
  if (t.includes('cro_unlock') || t === 'cro_unlock') return 'CDC_CRO_UNLOCK';
  if (t.includes('dpos_')) return 'CDC_DPOS_REWARD';
  if (t === 'reward' || t === 'rewards') return 'CDC_GENERIC_REWARD';
  if (t === 'cashback' || t === 'staking_reward') return 'CDC_GENERIC_STAKING_OR_CASHBACK';
  if (t === 'interest' || t === 'crypto_earn_interest') return 'CDC_EARN_INTEREST';
  return 'CDC_INCOME_OTHER';
}

export function rationaleForCryptoComIncome(typeNorm: string): string {
  if (typeNorm.toLowerCase() === 'rewards_platform_deposit_credited') {
    return (
      'Taxable income at FMV when received (Finland). Crypto.com mission / task reward ' +
      '(rewards_platform_deposit_credited). EUR basis becomes FIFO acquisition cost for later disposal.'
    );
  }
  return `Taxable income at FMV when received (Finland). Crypto.com export type “${typeNorm}”. Treated as income, not a purchase — EUR basis becomes acquisition cost for later disposal (FIFO).`;
}

/** Heuristic: looks like a reward/income string but not mapped to explicit income handling — review, do not auto-classify as income. */
export function looksLikeUnmappedRewardLikeType(typeNorm: string): boolean {
  const t = typeNorm.toLowerCase();
  if (!t) return false;
  if (
    /^(bought_|sold_|crypto_purchase|fiat_to_crypto|viban_purchase|deposit|crypto_to_fiat|payment|fee)/i.test(
      t
    ) ||
    /^deposit$/i.test(t) ||
    (t.includes('purchase') && !/reward|cashback|bonus|interest|staking|earn|payout|airdrop/i.test(t))
  ) {
    return false;
  }
  if (
    /reward|cashback|bonus|airdrop|rebate|interest|staking|earn|payout|referral|competition|campaign|gift|credit|promo|rakeback/i.test(
      t
    )
  ) {
    if (t.includes('transfer') || t.includes('withdraw')) {
      if (!/reward|cashback|bonus|interest|staking|earn|payout|airdrop/i.test(t)) return false;
    }
    return true;
  }
  return false;
}

export function fiSubreasonForExplorerIncome(
  tx: 'STAKING_REWARD' | 'AIRDROP' | 'INCOME'
): string {
  if (tx === 'STAKING_REWARD') return 'CHAIN_STAKING_REWARD';
  if (tx === 'AIRDROP') return 'CHAIN_AIRDROP';
  return 'CHAIN_OTHER_INCOME';
}

export function rationaleForExplorerIncome(
  tx: 'STAKING_REWARD' | 'AIRDROP' | 'INCOME'
): string {
  if (tx === 'STAKING_REWARD') {
    return 'Incoming tokens classified as staking or protocol reward; taxed as income at FMV at receipt (verify against wallet history).';
  }
  if (tx === 'AIRDROP') {
    return 'Incoming tokens classified as airdrop; generally taxable as income at FMV at receipt (confirm eligibility and exclusions).';
  }
  return 'Incoming tokens classified as miscellaneous income at FMV at receipt.';
}
